import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryExecution,
  CrossClientQueryPhase,
  CrossClientQueryPhases,
  CrossClientQueryResult,
  CrossClientQueryResultDisplay,
  CrossClientQueryResultMatch,
  CrossClientQueryResultMatches,
  CrossClientQueryResultStatuses,
} from '../../models/CrossClient'
import { User } from '../../models/User'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'

interface CrossClientResultsFilter {
  matches: CrossClientQueryResultMatch[]
  clientName: string | null
}

interface CrossClientResultsState {
  execution: CrossClientQueryExecution | null
  executionLoading: boolean
  results: EntityState<CrossClientQueryResultDisplay, string>
  resultsLoading: boolean
  lastResultUpdatedAt: string | null
  unableToMatchResultIds: string[]
  filter: CrossClientResultsFilter
  clients: EntityState<User, string>
  fetchedClientIds: Record<string, true>
  teammates: User[]
  clientsLoading: boolean
  teammatesLoading: boolean
  pollingEnabled: boolean
  paging: { page: number; size: number }
}

const crossClientResultsAdapter = createEntityAdapter<CrossClientQueryResultDisplay>()
const crossClientClientsAdapter = createEntityAdapter<User>()

const initialState: CrossClientResultsState = {
  execution: null,
  executionLoading: true,
  results: crossClientResultsAdapter.getInitialState(),
  resultsLoading: true,
  lastResultUpdatedAt: null,
  unableToMatchResultIds: [],
  filter: { matches: [CrossClientQueryResultMatches.MATCH], clientName: null },
  clients: crossClientClientsAdapter.getInitialState(),
  fetchedClientIds: {},
  teammates: [],
  clientsLoading: true,
  teammatesLoading: true,
  pollingEnabled: true,
  paging: { page: 1, size: 20 },
}

export const crossClientResultsSlice = createSlice({
  name: 'crossClientResults',
  initialState,
  reducers: {
    setCrossClientResultsExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executionLoading = action.payload
    },
    setCrossClientResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.resultsLoading = action.payload
    },
    startCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientResults: (state, action: PayloadAction<CrossClientQueryResult[]>) => {
      const { decomposition } = state.execution ?? {}
      if (!decomposition) {
        console.error('Cannot set cross client results without decomposition')
        return
      }
      if (!action.payload.length) {
        return
      }

      const { displayResults, unableToMatchResultIds, maxUpdatedAt } = action.payload.reduce(
        (acc, result) => {
          if (result.updatedAt > acc.maxUpdatedAt) {
            acc.maxUpdatedAt = result.updatedAt
          }
          if (result.status === CrossClientQueryResultStatuses.SUCCEEDED && result.answers && result.match) {
            acc.displayResults.push(result as CrossClientQueryResultDisplay)
          } else if (result.status === CrossClientQueryResultStatuses.NO_DATA || result.status === CrossClientQueryResultStatuses.FAILED) {
            acc.unableToMatchResultIds.push(result.id)
          }
          return acc
        },
        { displayResults: [] as CrossClientQueryResultDisplay[], unableToMatchResultIds: [] as string[], maxUpdatedAt: '' },
      )

      if (!state.lastResultUpdatedAt || maxUpdatedAt > state.lastResultUpdatedAt) {
        state.lastResultUpdatedAt = maxUpdatedAt
      }
      crossClientResultsAdapter.setMany(state.results, displayResults)
      state.unableToMatchResultIds = [...new Set([...state.unableToMatchResultIds, ...unableToMatchResultIds])]
    },
    setCrossClientResultsExecution: (state, action: PayloadAction<CrossClientQueryExecution | null>) => {
      state.execution = action.payload
    },
    setCrossClientResultsExecutionPhase: (state, action: PayloadAction<CrossClientQueryPhase>) => {
      if (state.execution) {
        state.execution.phase = action.payload
      }
    },
    setCrossClientResultsExecutionResultCount: (state, action: PayloadAction<number>) => {
      if (state.execution) {
        state.execution.resultCount = action.payload
      }
    },
    addCrossClientClients: (state, action: PayloadAction<User[]>) => {
      crossClientClientsAdapter.addMany(state.clients, action.payload)
    },
    addCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      state.fetchedClientIds = action.payload.reduce((acc, id) => ({ ...acc, [id]: true }), state.fetchedClientIds)
    },
    removeCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      const next = { ...state.fetchedClientIds }
      for (const id of action.payload) {
        delete next[id]
      }
      state.fetchedClientIds = next
    },
    setCrossClientTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientClientsLoading: (state, action: PayloadAction<boolean>) => {
      state.clientsLoading = action.payload
    },
    setCrossClientTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    setCrossClientResultsMatchFilter: (state, action: PayloadAction<CrossClientQueryResultMatch[]>) => {
      state.filter.matches = action.payload
    },
    setCrossClientResultsClientNameFilter: (state, action: PayloadAction<string | null>) => {
      state.filter.clientName = action.payload
    },
    setCrossClientPage: (state, action: PayloadAction<number>) => {
      state.paging.page = action.payload
    },
    setCrossClientPageSize: (state, action: PayloadAction<number>) => {
      state.paging.size = action.payload
    },
    resetCrossClientResults: () => initialState,
  },
})

export const {
  setCrossClientResultsExecutionLoading,
  setCrossClientResultsLoading,
  setCrossClientResults,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionPhase,
  setCrossClientResultsExecutionResultCount,
  addCrossClientClients,
  setCrossClientTeammates,
  setCrossClientClientsLoading,
  addCrossClientFetchedClientIds,
  removeCrossClientFetchedClientIds,
  setCrossClientTeammatesLoading,
  setCrossClientResultsMatchFilter,
  setCrossClientResultsClientNameFilter,
  setCrossClientPage,
  setCrossClientPageSize,
  resetCrossClientResults,
} = crossClientResultsSlice.actions

const crossClientResultSelectors = crossClientResultsAdapter.getSelectors((state: RootState) => state.crossClientResults.results)
const crossClientClientSelectors = crossClientClientsAdapter.getSelectors((state: RootState) => state.crossClientResults.clients)

export const selectCrossClientResults = (state: RootState) => crossClientResultSelectors.selectAll(state)

export const selectCrossClientResultsLoading = (state: RootState) => state.crossClientResults.resultsLoading

export const selectCrossClientResultsExecution = (state: RootState) => state.crossClientResults.execution

export const selectCrossClientTeammates = createSelector([(state: RootState) => state.crossClientResults.teammates], (teammates) => {
  return teammates.reduce(
    (teammateMap, teammate) => {
      teammateMap[teammate.id] = teammate
      return teammateMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientClients = createSelector([crossClientClientSelectors.selectAll], (clients) => {
  return clients.reduce(
    (clientMap, client) => {
      clientMap[client.id] = client
      return clientMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientFetchedClientIds = (state: RootState) => state.crossClientResults.fetchedClientIds

export const selectCrossClientFilter = (state: RootState) => state.crossClientResults.filter

export const selectCrossClientClientsLoading = (state: RootState) => state.crossClientResults.clientsLoading

export const selectCrossClientTeammatesLoading = (state: RootState) => state.crossClientResults.teammatesLoading

export const selectShouldPollCrossClientExecutionResults = (state: RootState) => {
  const execution = state.crossClientResults.execution
  if (!execution) {
    return false
  }

  const { phase } = execution
  if (phase === CrossClientQueryPhases.READY || phase === CrossClientQueryPhases.QUEUED || phase === CrossClientQueryPhases.PROCESSING) {
    return true
  }
  if (phase === CrossClientQueryPhases.ERRORED || phase === CrossClientQueryPhases.CANCELED) {
    return false
  }

  const results = crossClientResultSelectors.selectAll(state)
  const unableToMatchResultIds = state.crossClientResults.unableToMatchResultIds
  const expectedResultCount = execution.resultCount
  const receivedResultCount = results.length + unableToMatchResultIds.length
  return receivedResultCount < expectedResultCount
}

export const selectCrossClientResultsPollingEnabled = (state: RootState) => state.crossClientResults.pollingEnabled

export const selectCrossClientResultExecutionLoading = (state: RootState) => state.crossClientResults.executionLoading

export const selectLastCrossClientResultUpdatedAt = (state: RootState) => state.crossClientResults.lastResultUpdatedAt

const matchOrderMap = {
  [CrossClientQueryResultMatches.MATCH]: 1,
  [CrossClientQueryResultMatches.PARTIAL]: 2,
  [CrossClientQueryResultMatches.NO_MATCH]: 3,
}

export const selectCrossClientFilteredResults = createSelector(
  [selectCrossClientResults, selectCrossClientClients, selectCrossClientFilter],
  (results, clients, filter) => {
    const { matches, clientName } = filter
    return results
      .filter((results) => {
        if (!clientName) {
          return true
        }
        const client = clients[results.clientId]
        return client && getUserDisplayName(client).toLowerCase().includes(clientName.toLowerCase())
      })
      .filter((result) => matches.includes(result.match))
      .sort((result1, result2) => {
        return matchOrderMap[result1.match] - matchOrderMap[result2.match] || result1.id.localeCompare(result2.id)
      })
  },
)

export const selectCrossClientDisplayedResults = createSelector(
  [selectCrossClientFilteredResults, (state) => state.crossClientResults.paging],
  (results, paging) => {
    const { page, size } = paging
    return results.slice((page - 1) * size, page * size)
  },
)

export const selectCrossClientPartialResults = createSelector([selectCrossClientResults], (results) =>
  results.filter((r) => r.match === CrossClientQueryResultMatches.PARTIAL),
)

export const selectCrossClientUnableToMatchResultCount = (state: RootState) => state.crossClientResults.unableToMatchResultIds.length

export default crossClientResultsSlice.reducer
